import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { MessageSquare, Phone, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { PhoneInput } from "@/components/ui/phone-input";

export function WhatsAppNotificationsButton() {
  const { user } = useAuth();

  const [waPhoneDisplay, setWaPhoneDisplay] = useState<string | null>(null);
  const [waCountryCode, setWaCountryCode] = useState("+91");
  const [waRawPhone, setWaRawPhone] = useState("");
  const [waNotificationsEnabled, setWaNotificationsEnabled] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editCountryCode, setEditCountryCode] = useState("+91");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: ["user-profile-wa", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("phone_country_code, phone_number, onboarding_data")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!profileData) return;
    if (profileData.phone_number) {
      const code = profileData.phone_country_code || "91";
      const codeWithPlus = code.startsWith("+") ? code : `+${code}`;
      setWaCountryCode(codeWithPlus);
      setWaRawPhone(profileData.phone_number);
      setWaPhoneDisplay(`${codeWithPlus} ${profileData.phone_number}`);
      setEditCountryCode(codeWithPlus);
      setEditPhoneNumber(profileData.phone_number);
    }
    const onboardingData = profileData.onboarding_data as Record<string, unknown> | null;
    setWaNotificationsEnabled(onboardingData?.whatsapp_notifications_enabled !== false);
  }, [profileData]);

  const handleToggle = async (enabled: boolean) => {
    setWaNotificationsEnabled(enabled);
    const { data: current } = await supabase
      .from("profiles")
      .select("onboarding_data")
      .eq("id", user!.id)
      .single();
    const existingData = (current?.onboarding_data as Record<string, unknown>) || {};
    await supabase
      .from("profiles")
      .update({ onboarding_data: { ...existingData, whatsapp_notifications_enabled: enabled } })
      .eq("id", user!.id);
    toast.success(enabled ? "WhatsApp notifications enabled" : "WhatsApp notifications disabled");
  };

  const handleSave = async () => {
    if (!editPhoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    setSaving(true);
    const codeWithoutPlus = editCountryCode.replace("+", "");
    const { error } = await supabase
      .from("profiles")
      .update({ phone_country_code: codeWithoutPlus, phone_number: editPhoneNumber.trim() })
      .eq("id", user!.id);
    setSaving(false);
    if (error) {
      toast.error("Failed to update phone number");
      return;
    }
    setWaCountryCode(editCountryCode);
    setWaRawPhone(editPhoneNumber.trim());
    setWaPhoneDisplay(`${editCountryCode} ${editPhoneNumber.trim()}`);
    setIsEditing(false);
    toast.success("Phone number updated");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-2">
          <MessageSquare className="w-4 h-4" />
          WhatsApp
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <h4 className="font-semibold text-sm">WhatsApp Notifications</h4>
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Notifications</span>
            <Switch checked={waNotificationsEnabled} onCheckedChange={handleToggle} />
          </div>
          {isEditing ? (
            <div className="space-y-3">
              <PhoneInput
                countryCode={editCountryCode}
                phoneNumber={editPhoneNumber}
                onCountryCodeChange={setEditCountryCode}
                onPhoneNumberChange={setEditPhoneNumber}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditCountryCode(waCountryCode);
                    setEditPhoneNumber(waRawPhone);
                    setIsEditing(false);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                {waPhoneDisplay ? (
                  <span className="font-medium">{waPhoneDisplay}</span>
                ) : (
                  <span className="text-muted-foreground">No phone number linked</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setEditCountryCode(waCountryCode);
                  setEditPhoneNumber(waRawPhone);
                  setIsEditing(true);
                }}
              >
                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Campaign notifications are sent to this number via WhatsApp.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
